import React from 'react';
import { Linkedin, Twitter, Facebook, Globe, Phone, MapPin, Users, Calendar } from 'lucide-react';
import unknownLogo from '../../assets/unknown_logo.png';

interface CompetitorData {
  name: string;
  similarity: number;
  clean_description: string | null;
  logo_url: string | null;
  phone: string | null;
  founded_year: number | null;
  estimated_num_employees: number | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  website_url: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  facebook_url: string | null;
}

interface CompetitorCardWithAccessProps {
  competitor: CompetitorData;
}

const CompetitorCardWithAccess: React.FC<CompetitorCardWithAccessProps> = ({ competitor }) => {
  const location = [competitor.street_address, competitor.city, competitor.state, competitor.country]
    .filter(Boolean)
    .join(', ');

  return (
    <li className="p-6 border rounded-lg bg-white shadow flex flex-col">
      {/* Primera línea: Logo y Match */}
      <div className="flex items-center justify-between mb-6">
        <div className="w-16 h-16 rounded-full overflow-hidden">
        <img
            src={competitor.logo_url || unknownLogo}
            alt={`${competitor.name} logo`}
            className="w-full h-full object-cover"
            onError={(e) => (e.currentTarget.src = unknownLogo)}
            />
        </div>
        <span className="text-blue-600 font-bold text-xl">{competitor.similarity}% Match</span>
      </div>

      {/* Segunda línea: Nombre */}
      <h3 className="font-bold text-lg text-gray-800 mb-4">{competitor.name}</h3>

      {/* Tercera línea: Descripción */}
      <p className="text-sm text-gray-600 mb-6 text-justify">
        {competitor.clean_description || 'Sin descripción disponible'}
      </p>

      {/* Información con iconos */}
      <div className="space-y-4 text-sm">
        {competitor.phone && (
          <div className="flex items-center space-x-3">
            <Phone className="w-5 h-5 text-blue-600" />
            <span className="text-gray-700">{competitor.phone}</span>
          </div>
        )}
        {location && (
          <div className="flex items-center space-x-3">
            <MapPin className="w-5 h-5 text-blue-600" />
            <span className="text-gray-700">{location}</span>
          </div>
        )}
        {competitor.founded_year && (
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-blue-600" />
            <span className="text-gray-700">Fundado: {competitor.founded_year}</span>
          </div>
        )}
        {competitor.estimated_num_employees && (
          <div className="flex items-center space-x-3">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-gray-700">{competitor.estimated_num_employees} empleados</span>
          </div>
        )}
      </div>

      {/* Iconos de redes sociales */}
      <div className="mt-auto flex justify-center space-x-6 text-blue-600">
        {competitor.website_url && (
          <a href={competitor.website_url} target="_blank" rel="noopener noreferrer">
            <Globe className="w-6 h-6" />
          </a>
        )}
        {competitor.linkedin_url && (
          <a href={competitor.linkedin_url} target="_blank" rel="noopener noreferrer">
            <Linkedin className="w-6 h-6" />
          </a>
        )}
        {competitor.twitter_url && (
          <a href={competitor.twitter_url} target="_blank" rel="noopener noreferrer">
            <Twitter className="w-6 h-6" />
          </a>
        )}
        {competitor.facebook_url && (
          <a href={competitor.facebook_url} target="_blank" rel="noopener noreferrer">
            <Facebook className="w-6 h-6" />
          </a>
        )}
      </div>
    </li>
  );
};

export default CompetitorCardWithAccess;
